import { PriorityAction } from '@/lib/decisionEngine';
import { ActionRail } from '@/components/today/ActionRail';
import { TempPill, DealChip, HotActivityBar } from '@/components/leads/LeadVisual';
import { SmartCard } from '@/components/ui/smart-card';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getContactLabel, getFollowUpLabel } from '@/lib/timeLabels';
import { computeInaction } from '@/lib/inactionEngine';
import { INACTION_COPY } from '@/lib/inactionCopy';
import { MomentumIndicator } from './MomentumIndicator';
import { PriorityBadge } from './PriorityBadge';

interface MandatoryActionCardProps {
  action: PriorityAction;
  index: number;
  onCall: (action: PriorityAction) => void;
  onWhatsApp: (action: PriorityAction) => void;
  onMarkDone: (action: PriorityAction) => void;
  onDefer: (action: PriorityAction) => void;
  onCardClick: (action: PriorityAction) => void;
  onSendToPhone: (action: PriorityAction, actionType: 'call' | 'whatsapp') => void;
}

export function MandatoryActionCard({
  action,
  index,
  onCall,
  onWhatsApp,
  onMarkDone,
  onDefer,
  onCardClick,
  onSendToPhone,
}: MandatoryActionCardProps) {
  const isHot = (action.lead.temperature ?? "").toLowerCase() === "hot";
  const lead = action.lead as { is_completed?: boolean; status?: string; completed_at?: string | null };
  const isCompleted =
    lead.is_completed === true || lead.status === 'done' || lead.completed_at != null;

  return (
    <SmartCard
      spotlight
      glow={index === 0}
      hover
      interactive
      className={cn(
        "group p-4 cursor-pointer relative overflow-visible card-shell dsos-card dsos-lead-outline dsos-hover-lift dsos-focus-ring",
        isCompleted && "opacity-60"
      )}
      onClick={() => onCardClick(action)}
      style={{ writingMode: 'horizontal-tb' }}
    >
      <div className="flex items-start gap-4 relative z-10">
        {/* Priority number */}
        <div
          className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-200",
            index === 0
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-secondary text-muted-foreground"
          )}
        >
          {index + 1}
        </div>

        <div className="flex-1 min-w-0 space-y-2.5" style={{ writingMode: 'horizontal-tb' }}>
          {/* Header row: temp pill + inaction + deal chip (premium, no hot glow) */}
          <div className="flex items-center gap-2 flex-wrap" style={{ writingMode: 'horizontal-tb' }}>
            <TempPill value={action.lead.temperature as "hot" | "warm" | "cold" | "stale" | null} />

            {/* Inaction state badge - neutral pill style */}
            {(() => {
              const inaction = computeInaction({
                lead: {
                  ...(action.lead as any),
                },
              });

              const baseBadgeClasses =
                "text-[9px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full inline-flex items-center gap-1 border border-black/10 dark:border-white/10";

              const variantClasses =
                inaction.state === 'FRESH'
                  ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 dark:bg-emerald-500/5"
                  : inaction.state === 'DUE'
                  ? "text-amber-700 dark:text-amber-400 bg-amber-500/10 dark:bg-amber-500/5"
                  : inaction.state === 'OVERDUE'
                  ? "text-red-600 dark:text-red-400 bg-red-500/10 dark:bg-red-500/5"
                  : "text-muted-foreground bg-muted-foreground/10 dark:bg-muted-foreground/5";

              return (
                <span
                  className={cn(baseBadgeClasses, variantClasses)}
                  style={{ writingMode: 'horizontal-tb' }}
                  aria-label={`Inaction state: ${inaction.state}`}
                >
                  <span
                    className={cn(
                      "w-1.5 h-1.5 rounded-full",
                      inaction.state === 'FRESH' && "bg-emerald-400",
                      inaction.state === 'DUE' && "bg-amber-400",
                      inaction.state === 'OVERDUE' && "bg-red-400",
                      inaction.state === 'STALE' && "bg-muted-foreground/80"
                    )}
                  />
                  {inaction.state !== 'OVERDUE' ? inaction.state : null}
                </span>
              );
            })()}

            <DealChip value={action.dealValue} currency="EUR" />
          </div>

          {/* Lead name */}
          <h4 className={cn(
            "font-semibold text-base leading-tight break-words",
            isCompleted ? "text-muted-foreground line-through" : "text-foreground"
          )}>
            {action.lead.name}
          </h4>

          {/* WHY NOW - Inaction-aware urgency microcopy (horizontal text flow, improved contrast) */}
          {(() => {
            const neverContacted = !action.lead.last_interaction_at;
            const contactLabel = getContactLabel({
              neverContacted,
              lastInteractionAt: action.lead.last_interaction_at,
            });
            const followUpLabel = getFollowUpLabel({
              nextFollowUpAt: (action.lead as any).next_follow_up_at,
              nextActionAt: action.lead.next_action_at,
            });

            const inaction = computeInaction({
              lead: {
                ...(action.lead as any),
              },
            });
            
            // Filter out "999" patterns from priority reasons
            const priorityReasons = (action as any).priorityReasons || [];
            const filteredReasons = priorityReasons
              .filter((r: { reason: string }) => !r.reason.includes('999') && !r.reason.match(/\b999\b/))
              .slice(0, 2);
            
            return (
              <div className="space-y-1.5">
                {/* Why now - DSOS style sentence (primary) */}
                <div className="text-[11px] text-foreground/80 font-medium leading-relaxed">
                  <span className="text-foreground/60 font-semibold inline-block mr-1.5">Why now:</span>
                  <span className="inline whitespace-normal">
                    {inaction.hasTimingData ? inaction.whyNow : INACTION_COPY.fallback.noTimingData}
                  </span>
                </div>
                
                {/* Single source: timing (Due in / Overdue by) - one line only */}
                {inaction.hasTimingData && (inaction.dueInLabel || inaction.overdueByLabel) ? (
                  <div className="text-[11px] text-foreground/60 leading-relaxed flex items-center gap-1.5">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    {inaction.overdueByLabel ? (
                      <span className="whitespace-normal">
                        {INACTION_COPY.labels.overdueBy(inaction.overdueByLabel)}
                      </span>
                    ) : inaction.dueInLabel ? (
                      <span className="whitespace-normal">
                        {INACTION_COPY.labels.dueIn(inaction.dueInLabel)}
                      </span>
                    ) : null}
                  </div>
                ) : followUpLabel ? (
                  <div className="text-[11px] text-foreground/60 leading-relaxed">
                    <Clock className="w-3 h-3 inline mr-1" />
                    <span>{followUpLabel}</span>
                  </div>
                ) : null}
                
                {/* Priority reasons (if any, after filtering) */}
                {filteredReasons.length > 0 && (
                  <div className="text-[11px] text-foreground/60 leading-relaxed">
                    {filteredReasons.map((r: { reason: string; weight: number }, idx: number) => {
                      // Format urgency reasons for better readability
                      let formattedReason = r.reason;
                      if (r.reason.includes('Overdue')) {
                        formattedReason = `At risk: ${r.reason.toLowerCase()}`;
                      } else if (r.reason.includes('Never contacted')) {
                        formattedReason = 'At risk: never contacted';
                      } else if (r.reason.includes('Stale')) {
                        const daysMatch = r.reason.match(/(\d+)\s*days/);
                        if (daysMatch && parseInt(daysMatch[1]) < 365) {
                          formattedReason = `Overdue follow-up: ${daysMatch[1]}d`;
                        } else {
                          return null; // Skip if stale days >= 365
                        }
                      } else if (r.reason.includes('deal')) {
                        formattedReason = r.reason; // Keep deal value as-is
                      }
                      
                      return (
                        <span key={idx} className="inline whitespace-normal">
                          {formattedReason}
                          {idx < filteredReasons.length - 1 && ' · '}
                        </span>
                      );
                    }).filter(Boolean)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Momentum Indicator (replaces execution debt) */}
          {'deal' in action && action.deal && (
            <div className="mt-2">
              <MomentumIndicator action={action as any} />
            </div>
          )}
        </div>

        <ActionRail
          action={action}
          onCall={onCall}
          onWhatsApp={onWhatsApp}
          onMarkDone={onMarkDone}
          onDefer={onDefer}
          onSendToPhone={onSendToPhone}
          primaryCelebration={index === 0}
          stopPropagation
        />
      </div>
      <HotActivityBar active={isHot} />
      {/* Priority badge - bottom right of card */}
      {(action as any).priority && (action as any).priorityReasons && (
        <div className="absolute bottom-2 right-2 z-20">
          <PriorityBadge
            priority={(action as any).priority}
            reasons={(action as any).priorityReasons}
            hasFutureFollowUp={(action as any).hasFutureFollowUp}
            size="sm"
          />
        </div>
      )}
    </SmartCard>
  );
}
